import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { ChevronDownIcon } from '@heroicons/react/16/solid';

import { StudentExamOrdering } from '@/data/services/studentExamServices';
import { OrderingKey } from '@/models/Filter';
import { Button, TableColumn } from '@/components/ui/daisy';

import { KlassCell } from '@/components/shared/pendencies/tables/cells';
import {
  SentAtCell,
  StudentCell,
} from '@/components/shared/pendencies/tables/cells/activities';
import {
  ExamCell,
  ExamCorrectActionCell,
} from '@/components/shared/pendencies/tables/cells/exams';
import { StudentExamWithKlass } from '@/components/shared/pendencies/tables/types';

export type ExamPendencyColumnKey =
  | 'exam'
  | 'klass'
  | 'student'
  | 'deliveredAt'
  | 'actions';

type UseExamPendencyColumnsParams = {
  ordering?: StudentExamOrdering;
  onToggleOrdering: (field: OrderingKey<StudentExamOrdering>) => void;
};

export default function useExamPendencyColumns({
  ordering,
  onToggleOrdering,
}: UseExamPendencyColumnsParams): TableColumn<
  StudentExamWithKlass,
  ExamPendencyColumnKey
>[] {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pendencies.exams',
  });

  return useMemo(
    () => [
      {
        key: 'exam',
        header: t('columns.exam'),
        Cell: ExamCell,
        headerClassName: 'w-[30%]',
      },
      {
        key: 'klass',
        header: t('columns.klass'),
        Cell: KlassCell,
        headerClassName: 'w-[24%]',
      },
      {
        key: 'student',
        header: t('columns.student'),
        Cell: StudentCell,
        headerClassName: 'w-[28%]',
      },
      {
        key: 'deliveredAt',
        header: (
          <Button
            variant="ghost"
            size="sm"
            className="font-bold"
            onClick={() => onToggleOrdering('finished_at')}
          >
            {t('columns.deliveredAt')}
            <ChevronDownIcon
              className={twJoin(
                'w-4 transition-transform',
                ordering === 'finished_at' && 'rotate-180',
              )}
            />
          </Button>
        ),
        Cell: SentAtCell,
        headerClassName: 'w-[18%]',
        cellClassName: 'whitespace-nowrap',
      },
      {
        key: 'actions',
        header: t('columns.actions'),
        Cell: ExamCorrectActionCell,
        headerClassName: 'w-32 whitespace-nowrap',
        cellClassName: 'w-32 whitespace-nowrap',
      },
    ],
    [t, ordering, onToggleOrdering],
  );
}
